// src/oura/components/search/MonthlyReport.tsx
//
// The monthly report (ourteam-backend#6426, child 6/6): the month's search
// and traffic figures in one read, frozen when staff send it.
//
// Staff see it on Search performance as a preview of the newest finished
// check, with "Send to <client>". Sending publishes that check through the
// existing gate and tells the client by bell and email, so the button says
// so before it goes. The client reads the copy that was sent, on Reports.
//
// Every block renders only when the report carries it: absent is unmeasured,
// never a zero. Facts only (Jack, 21 Oct 2026): nothing here says what we
// will do next.

import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDownIcon, ArrowUpIcon, FileBarChartIcon, FlagIcon, SendIcon } from "lucide-react";
import { getMonthlyReport, sendMonthlyReport, type MonthlyReport, type MonthlyReportMover } from "@service/aeo/monthlyReport";
import { Bento, FoldCard, PillButton, Surface, Tile, Tiles, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { dayDate, shortDate } from "../../lib/oura";
import { aiVisits, num } from "./format";
import { PctChange } from "./PctChange";
import { LandingPagesCard, SourcesCard, Versus } from "./TrafficSources";

/** The report's own figures in the order a client reads them. */
export function MonthlyReportView({ report, isStaff }: { report: MonthlyReport; isStaff?: boolean }) {
    const search = report.search;
    const traffic = report.traffic;
    const summary = report.monthSummary;
    const ai = traffic ? aiVisits(traffic) : null;
    const days = search?.window?.days ?? 28;
    const up = search?.moved?.up ?? [];
    const slipped = search?.moved?.slipped ?? [];
    const near = search?.closeToPageOne ?? [];
    const markers = traffic?.markers ?? [];
    const tiles = [
        search && (
            <Tile
                key="clicks"
                label={isStaff ? "Clicks" : "Clicks from Google"}
                value={num(search.totals.clicks)}
                sub={search.totals.clicksDeltaPct !== null ? <PctChange percent={search.totals.clicksDeltaPct * 100} days={days} /> : `${days} days`}
            />
        ),
        search && (
            <Tile
                key="impressions"
                label={isStaff ? "Impressions" : "Times shown on Google"}
                value={num(search.totals.impressions)}
                sub={search.totals.impressionsDeltaPct !== null ? <PctChange percent={search.totals.impressionsDeltaPct * 100} days={days} /> : `${days} days`}
            />
        ),
        traffic && (
            <Tile
                key="visits"
                label={isStaff ? "Sessions, GA4" : "All visits to your site"}
                value={num(traffic.visits)}
                sub={traffic.deltaPct !== null ? <PctChange percent={traffic.deltaPct} days={traffic.window?.days ?? 28} /> : "28 days"}
            />
        ),
        ai && (
            <Tile
                key="ai"
                label={isStaff ? "AI referrals, GA4" : "Visits from AI assistants"}
                value={num(ai.now)}
                sub={ai.before !== null && ai.before > 0 ? <span><Versus now={ai.now} before={ai.before} /> on the 28 days before</span> : "28 days"}
            />
        ),
        traffic?.keyEvents && (
            <Tile
                key="enquiries"
                label={isStaff ? "Key events, GA4" : "Enquiries and bookings"}
                value={num(traffic.keyEvents.count)}
                sub={traffic.keyEvents.prevCount !== null && traffic.keyEvents.prevCount > 0
                    ? <span><Versus now={traffic.keyEvents.count} before={traffic.keyEvents.prevCount} /> on the 28 days before</span>
                    : "28 days"}
            />
        ),
    ].filter(Boolean);

    return (
        <div className="flex flex-col gap-4">
            {summary && (
                <div className="flex flex-col gap-2">
                    <p className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{summary.line1}</p>
                    {summary.line2 && <p className={cn(TYPE.tb, "m-0 text-gray-700")}>{summary.line2}</p>}
                    {summary.writeUp && (
                        <dl className="m-0 grid gap-x-4 gap-y-1.5 sm:grid-cols-[max-content_1fr]">
                            {([["What happened", summary.writeUp.happened], ["Why", summary.writeUp.why], ["Worth knowing", summary.writeUp.worthKnowing]] as const).map(([label, text]) => (
                                <div key={label} className="contents">
                                    <dt className={cn(TYPE.tc, "font-semibold text-gray-500")}>{label}</dt>
                                    <dd className={cn(TYPE.tb, "m-0 text-gray-900")}>{text}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            )}
            {tiles.length > 0 && <Tiles cols={tiles.length === 4 ? 4 : tiles.length >= 3 ? 3 : 2}>{tiles}</Tiles>}
            {(up.length > 0 || slipped.length > 0) && (
                <div className="grid gap-4 sm:grid-cols-2">
                    {up.length > 0 && <MoverList title={isStaff ? "Climbed" : "Searches that climbed"} rows={up} up />}
                    {slipped.length > 0 && <MoverList title={isStaff ? "Slipped" : "Searches that slipped"} rows={slipped} up={false} />}
                </div>
            )}
            {near.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    <span className={cn(TYPE.tc, "font-semibold text-gray-700")}>Close to page one of Google</span>
                    {near.map((n) => (
                        <div key={n.query} className={cn(TYPE.tb, "flex justify-between gap-3")}>
                            <span className="min-w-0 truncate text-gray-900">{n.query}</span>
                            <span className="shrink-0 tabular-nums text-gray-500">Position {n.position}</span>
                        </div>
                    ))}
                </div>
            )}
            {traffic && (traffic.sources || traffic.aiReferrals || traffic.landingPages) && (
                <Bento layout="c2">
                    {(traffic.sources || traffic.aiReferrals) && <SourcesCard traffic={traffic} isStaff={isStaff} />}
                    {traffic.landingPages && <LandingPagesCard pages={traffic.landingPages} isStaff={isStaff} />}
                </Bento>
            )}
            {markers.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    <span className={cn(TYPE.tc, "font-semibold text-gray-700")}>What happened when</span>
                    {markers.map((m) => (
                        <div key={`${m.kind}-${m.id ?? m.date}-${m.label}`} className={cn(TYPE.tb, "flex items-center gap-2")}>
                            <FlagIcon aria-hidden size={13} className={m.kind === "work" ? "text-blue-600" : "text-amber-600"} />
                            <span className="w-24 shrink-0 whitespace-nowrap tabular-nums text-gray-500">{shortDate(m.date)}</span>
                            <span className="min-w-0 flex-1 text-gray-900">{m.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function MoverList({ title, rows, up }: { title: string; rows: MonthlyReportMover[]; up: boolean }) {
    if (rows.length === 0) return null;
    const Icon = up ? ArrowUpIcon : ArrowDownIcon;
    return (
        <div className="flex flex-col gap-1.5">
            <span className={cn(TYPE.tc, "flex items-center gap-1.5 font-semibold text-gray-700")}>
                <Icon aria-hidden size={13} className={up ? "text-emerald-600" : "text-rose-600"} />
                {title}
            </span>
            {rows.map((r) => (
                <div key={r.query} className={cn(TYPE.tb, "flex justify-between gap-3")}>
                    <span className="min-w-0 truncate text-gray-900">{r.query}</span>
                    <span className="shrink-0 tabular-nums text-gray-500">
                        {Math.round(r.was)} to {Math.round(r.now)}
                    </span>
                </div>
            ))}
        </div>
    );
}

/**
 * Staff: preview the newest finished check's report and send it. Renders
 * nothing until the route answers. The caller keys it by business, so a
 * switch remounts it and no report or open confirmation carries over.
 */
export function MonthlyReportPanel({ businessId, businessName }: { businessId: string; businessName: string }) {
    const [report, setReport] = useState<MonthlyReport | null | undefined>(undefined);
    const [open, setOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [readError, setReadError] = useState<string | null>(null);
    // Only the newest read may land, so a slow earlier one cannot overwrite it.
    const readSeq = useRef(0);

    const load = useCallback(async () => {
        const mine = ++readSeq.current;
        setReadError(null);
        const r = await getMonthlyReport(businessId);
        if (mine !== readSeq.current) return;
        if (!r.ok) return setReadError(r.message);
        setReport(r.report);
    }, [businessId]);
    useEffect(() => {
        void load();
        return () => { readSeq.current += 1; };
    }, [load]);

    if (readError) {
        return (
            <Surface>
                <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>Monthly report</span>
                <p className={cn(TYPE.tc, "m-0 mt-1 text-rose-700")}>{readError}</p>
                <div className="mt-3">
                    <PillButton size="sm" onClick={() => void load()}>Try again</PillButton>
                </div>
            </Surface>
        );
    }
    if (report === undefined) return null;
    if (report === null) {
        return (
            <Surface>
                <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>Monthly report</span>
                <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>Nothing to send yet. The report is built from the newest finished AI check.</p>
            </Surface>
        );
    }

    async function send() {
        setBusy(true);
        setError(null);
        const r = await sendMonthlyReport(businessId);
        setBusy(false);
        setConfirming(false);
        if (!r.ok) return setError(r.message);
        await load();
    }

    const sentOn = dayDate(report.sentAt);
    const status = sentOn
        ? `Sent to ${businessName} on ${sentOn}.`
        : report.published
            ? `Not sent yet. The AI check it covers is already visible to ${businessName}.`
            : `Not sent yet. Sending also shows ${businessName} the AI check it covers.`;

    return (
        <Surface>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-col">
                    <span className={cn(TYPE.tb, "flex items-center gap-2 font-semibold text-gray-900")}>
                        <FileBarChartIcon aria-hidden size={16} className="text-blue-600" />
                        Monthly report
                    </span>
                    <span className={cn(TYPE.tc, "text-gray-500")}>{status}</span>
                </div>
                <div className="flex gap-2">
                    <PillButton size="sm" onClick={() => setOpen((o) => !o)}>{open ? "Hide preview" : "Preview"}</PillButton>
                    {!sentOn && !confirming && (
                        <PillButton size="sm" variant="primary" icon={SendIcon} writes onClick={() => setConfirming(true)}>
                            Send to {businessName}
                        </PillButton>
                    )}
                </div>
            </div>
            {confirming && (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 px-3.5 py-2.5">
                    <span className={cn(TYPE.tb, "min-w-0 flex-1 text-amber-900")}>
                        {businessName} gets an email and a notice in Oura{report.published ? "" : ", and sees the AI check this report covers"}. This cannot be taken back.
                    </span>
                    <PillButton size="sm" variant="primary" busy={busy} writes onClick={() => void send()}>Send now</PillButton>
                    <PillButton size="sm" disabled={busy} onClick={() => setConfirming(false)}>Cancel</PillButton>
                </div>
            )}
            {error && <p className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>{error}</p>}
            {open && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                    <MonthlyReportView report={report} isStaff />
                </div>
            )}
        </Surface>
    );
}

/** Client: the report staff sent, on Reports. Renders nothing until one was sent. Keyed by business by the caller. */
export function ClientMonthlyReport({ businessId }: { businessId: string }) {
    const [report, setReport] = useState<MonthlyReport | null>(null);
    const [readFailed, setReadFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let live = true;
        setReadFailed(false);
        void getMonthlyReport(businessId).then((r) => {
            if (!live) return;
            if (r.ok) setReport(r.report);
            else setReadFailed(true);
        });
        return () => { live = false; };
    }, [businessId, attempt]);
    // A failed read is not "no report": say so and offer the retry.
    if (readFailed) {
        return (
            <Surface>
                <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>Your monthly report could not be loaded.</span>
                <div className="mt-3">
                    <PillButton size="sm" onClick={() => setAttempt((n) => n + 1)}>Try again</PillButton>
                </div>
            </Surface>
        );
    }
    // Only a sent copy. Staff viewing as the merchant get the unsent preview from the same route.
    if (!report?.sentAt) return null;
    const sentOn = dayDate(report.sentAt);
    return (
        <FoldCard
            id="monthly-report"
            title="Your monthly report"
            icon={{ icon: FileBarChartIcon, area: "google" }}
            right={sentOn ? `Sent ${sentOn}` : undefined}
        >
            <MonthlyReportView report={report} />
        </FoldCard>
    );
}
